import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, injectIntl } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import {
  Button, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledButtonDropdown,
} from 'reactstrap';

import Loading from 'src/components/Loading';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';
import AcceptNominatedTradeMutation from 'src/mutations/AcceptNominatedTradeMutation';
import CancelNominatedTradeMutation from 'src/mutations/CancelNominatedTradeMutation';
import RejectNominatedTradeMutation from 'src/mutations/RejectNominatedTradeMutation';
import FlashesStore from 'src/stores/FlashesStore';
import {
  TRADE_RULE_ACCEPT, TRADE_RULE_CANCEL, TRADE_RULE_REJECT, TRADE_RULE_STATE_PROPOSED,
  USER_TYPE_CUSTOMER,
} from 'src/util/constants';
import isActive from 'src/util/isActive';

import TradeRuleTimelineStepProposed from '../TradeRuleTimeline/TradeRuleTimelineStepProposed';
import { getTradeRuleState, tradeRuleActions } from '../helpers';

const getRules = (rules) => {
  if (rules) {
    const { buy, sell } = rules;
    if (buy && buy.length > 0) {
      return buy[0];
    }
    if (sell && sell.length > 0) {
      return sell[0];
    }
  }
  return {};
};

/**
 * Sorts the actions in the provided trade rules object based on a predefined display order.
 * @param {object} rules - The trade rules object containing the actions to be sorted.
 * @returns {object} - The original trade rules object sorted by actions.
 */
export const orderTradeRulesByActions = (rules) => {
  if (!rules) return null;
  const { actions } = rules || {};
  if (!actions) return rules;
  const displayList = [TRADE_RULE_ACCEPT, TRADE_RULE_REJECT, TRADE_RULE_CANCEL];
  actions.sort((a, b) => displayList.indexOf(a) - displayList.indexOf(b));

  return rules;
};

class TradeRuleShow extends React.Component {
  static tradeRules(uuid, proposal) {
    const rules = { buy: [], sell: [], count: 0 };

    if (!proposal) { return rules; }

    if (proposal.state !== TRADE_RULE_STATE_PROPOSED) { return rules; }

    const actions = [];
    if (proposal.buyer.user.uuid === uuid) {
      if (proposal.buyer.tradePoint.uuid === '') {
        actions.push(TRADE_RULE_REJECT);
        actions.push(TRADE_RULE_ACCEPT);
      } else {
        actions.push(TRADE_RULE_CANCEL);
      }
    }
    if (proposal.seller.user.uuid === uuid) {
      if (proposal.seller.tradePoint.uuid === '') {
        actions.push(TRADE_RULE_REJECT);
        actions.push(TRADE_RULE_ACCEPT);
      } else {
        actions.push(TRADE_RULE_CANCEL);
      }
    }

    if (proposal.seller.user.uuid === proposal.proposedBy.uuid) {
      rules.sell.push({ ...proposal, actions });
    } else {
      rules.buy.push({ ...proposal, actions });
    }
    return rules;
  }

  constructor(props) {
    super(props);
    this.state = {
      processing: false,
    };
  }

  // eslint-disable-next-line class-methods-use-this -- consistency in handlers.
  handleNominatedTradeMutationFailure = (error) => {
    this.setState({ processing: false });
    FlashesStore.flash(FlashesStore.ERROR, error);
  };

  // eslint-disable-next-line class-methods-use-this -- consistency in handlers.
  handleCancelNominatedTradeMutationSuccess = (response) => {
    this.setState({ processing: false });
    const { uuid } = response.cancelNominatedTrade;
    const { intl } = this.props;
    const successMessage = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_show.response_message.cancelled', defaultMessage: 'Cancelled peer-to-peer trade rule proposal with id: {uuid}.' }, { uuid });

    FlashesStore.flash(
      FlashesStore.SUCCESS,
      successMessage,
    );
  };

  // eslint-disable-next-line class-methods-use-this -- consistency in handlers.
  handleRejectNominatedTradeMutationSuccess = (response) => {
    this.setState({ processing: false });
    const { uuid } = response.rejectNominatedTrade;
    const { intl } = this.props;
    const successMessage = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_show.response_message.rejected', defaultMessage: 'Rejected peer-to-peer trade rule proposal with id: {uuid}.' }, { uuid });

    FlashesStore.flash(
      FlashesStore.SUCCESS,
      successMessage,
    );
  };

  // eslint-disable-next-line class-methods-use-this -- consistency in handlers.
  handleAcceptNominatedTradeMutationSuccess = (response) => {
    this.setState({ processing: false });
    const { uuid } = response.acceptNominatedTrade;
    const { intl } = this.props;
    const successMessage = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_show.response_message.accepted', defaultMessage: 'Accepted peer-to-peer trade rule proposal with id: {uuid}.' }, { uuid });

    FlashesStore.flash(
      FlashesStore.SUCCESS,
      successMessage,
    );
  };

  tradeRuleAction = (rule, action, opts = {}) => {
    const { id, uuid } = rule;
    const tradeRuleIdSet = { id, uuid };
    switch (action) {
      case TRADE_RULE_CANCEL:
        this.setState({ processing: true });
        CancelNominatedTradeMutation(
          tradeRuleIdSet,
          this.handleCancelNominatedTradeMutationSuccess,
          this.handleNominatedTradeMutationFailure,
        );
        break;
      case TRADE_RULE_REJECT:
        this.setState({ processing: true });
        RejectNominatedTradeMutation(
          tradeRuleIdSet,
          this.handleRejectNominatedTradeMutationSuccess,
          this.handleNominatedTradeMutationFailure,
        );
        break;
      case TRADE_RULE_ACCEPT:
        this.setState({ processing: true });
        AcceptNominatedTradeMutation(
          { ...tradeRuleIdSet, tradePointId: opts.tradePointId },
          this.handleAcceptNominatedTradeMutationSuccess,
          this.handleNominatedTradeMutationFailure,
        );
        break;
      default:
        FlashesStore.flash(
          FlashesStore.ERROR,
          `Action ${action} is not possible`,
        );
    }
  };

  /**
   * @param {object} rule - trade rule
   * @returns {React.ReactComponentElement} Trade rule action buttons
   */
  tradeRuleActionButton = (rule) => {
    const {
      uuid, state, buyer, seller,
    } = rule;

    const { processing } = this.state;
    const { intl } = this.props;

    const counterPartyTradePointId = [buyer?.tradePoint?.uuid, seller?.tradePoint?.uuid]
      .filter(Boolean)[0];

    if (state !== TRADE_RULE_STATE_PROPOSED || !counterPartyTradePointId) {
      return null;
    }

    const { viewer } = this.props;
    const { meters: meterConn } = viewer;
    const meterNodes = (!meterConn || !meterConn.edges)
      ? [] : meterConn.edges.map((edge) => (edge.node));

    const options = meterNodes.reduce((acc, el) => {
      const {
        identifier, title, tradePointId, active,
      } = el;
      if (tradePointId === counterPartyTradePointId) { return acc; }
      if (!el.property || !el.property.uuid) { return acc; }
      acc[el.property.uuid] = acc[el.property.uuid] || { property: el.property, tradePoints: [] };
      acc[el.property.uuid].tradePoints.push({
        identifier, title, tradePointId, active,
      });
      return acc;
    }, {});

    const buttons = [];
    const tradeRuleActionLookup = {
      TRADE_RULE_REJECT: intl.formatMessage({ id: 'trade_rule.trade_rule_customer_show.trade_rule_action_button.cancel.label', defaultMessage: 'Reject' }),
      TRADE_RULE_CANCEL: intl.formatMessage({ id: 'trade_rule.trade_rule_customer_show.trade_rule_action_button.close.label', defaultMessage: 'Close' }),
    };

    rule?.actions?.forEach((action) => {
      if (action === TRADE_RULE_ACCEPT) {
        buttons.push(
          <UncontrolledButtonDropdown key={`${uuid}-${action}`}>
            <DropdownToggle caret disabled={processing}>
              {intl.formatMessage({ id: 'trade_rule.trade_rule_customer_show.trade_rule_action_button.accept.label', defaultMessage: 'Accept' })}
            </DropdownToggle>
            <DropdownMenu>
              {Object.values(options).map((opts) => {
                const { property, tradePoints } = opts;
                return (
                  <>
                    <DropdownItem header key={`${uuid}-${action}-${property.uuid}`}>{property.title}</DropdownItem>
                    {tradePoints.map((tp) => (
                      <DropdownItem
                        onClick={() => (
                          this.tradeRuleAction(rule, action, { tradePointId: tp.tradePointId })
                        )}
                        disabled={!isActive(tp.active)}
                        key={`${uuid}-${action}-${tp.tradePointId}`}
                      >
                        {`${tp.title} (${tp.identifier})`}
                      </DropdownItem>
                    ))}
                  </>
                );
              })}
            </DropdownMenu>
          </UncontrolledButtonDropdown>,
        );
        return;
      }
      buttons.push(
        <Button onClick={() => this.tradeRuleAction(rule, action)} key={`${uuid}-${action}`} disabled={processing}>
          {tradeRuleActionLookup[action]}
        </Button>,
      );
    });

    return tradeRuleActions(buttons);
  };

  render() {
    if (this.error) {
      return <div><FormattedMessage id="error.title" defaultMessage="Error!" /></div>;
    }
    if (!this.props) {
      return <Loading />;
    }

    const { intl, router, viewer } = this.props;
    const { rule: tradeRule, viewerUser } = viewer;
    const { uuid } = tradeRule;
    const { uuid: userId } = viewerUser;
    const { formatMessage } = intl;

    const meterNodes = viewer.meters.edges.map((edge) => edge.node);
    const metersMap = {};
    meterNodes.forEach((item) => {
      const { tradePointId } = item;
      metersMap[tradePointId] = item;
    });

    if (!viewerUser || viewerUser.type !== USER_TYPE_CUSTOMER) {
      FlashesStore.flash(FlashesStore.ERROR, <FormattedMessage id="trade_rule_customer_proposals.view.restriction.text" defaultMessage="only customers can view their proposals" />);
      router.replace('/properties');
    }

    const rules = TradeRuleShow.tradeRules(userId, tradeRule);

    const {
      tradeType, clauses, buyer, seller, proposedAt, proposedBy,
    } = tradeRule;

    const tradeRuleState = getTradeRuleState(tradeRule);
    const finalRules = getRules(rules);
    const orderedRulesByActions = orderTradeRulesByActions(finalRules);
    const buttons = this.tradeRuleActionButton(orderedRulesByActions);
    const pageTitle = formatMessage({ id: 'trade_rule_customer_show.page_title', defaultMessage: 'Enosi - Trade Rule' });

    return (
      <>
        <Helmet>
          <meta charSet="utf-8" />
          <title>{pageTitle}</title>
        </Helmet>

        <Breadcrumbs
          breadcrumbs={[
            { name: <FormattedMessage id="common.entities.trade_rules.label" defaultMessage="Trade Rules" />, path: '/trade-rules' },
            { name: uuid },
          ]}
        />

        <Heading alias={<FormattedMessage id="trade_rule.header" defaultMessage="Trade Rule" />} context={`${formatMessage({ id: 'common.entities.rule.label', defaultMessage: 'Rule' })} ${uuid}`} />

        {buttons
          && (
            <ol className="m-0 p-0">
              <TradeRuleTimelineStepProposed
                metersMap={metersMap}
                ruleId={uuid}
                tradeType={tradeType}
                clauses={clauses}
                buyer={buyer}
                seller={seller}
                timestamp={proposedAt}
                user={proposedBy}
                isCurrentStep={tradeRuleState === TRADE_RULE_STATE_PROPOSED}
                hasNextStep={tradeRuleState !== TRADE_RULE_STATE_PROPOSED || !!buttons}
              />
              {buttons}
            </ol>
          )}
      </>
    );
  }
}

TradeRuleShow.propTypes = {
  viewer: PropTypes.shape({
    viewerUser: PropTypes.shape({
      uuid: PropTypes.string,
      type: PropTypes.string,
    }),
    meters: PropTypes.shape({
      edges: PropTypes.arrayOf({
        node: PropTypes.shape({
          uuid: PropTypes.string,
          identifier: PropTypes.string,
          tradePointId: PropTypes.string,
          active: PropTypes.shape({
            start: PropTypes.number,
            finish: PropTypes.number,
          }),
        }),
      }),
    }),
    rule: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  }).isRequired,
  router: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  intl: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

TradeRuleShow.defaultProps = {};

export default injectIntl(createFragmentContainer(
  TradeRuleShow,
  {
    viewer: graphql`
      fragment TradeRuleCustomerShow_viewer on Viewer @argumentDefinitions(
        uuid: { type: "ID!"}
      ) {
        id
        viewerUser { uuid type }
        meters(first: 500) {
          edges {
            node {
              uuid
              identifier
              title
              tradePointId
              active { start finish }
              property {
                uuid
                title
                active { start finish }
              }
            }
          }
        }
        rule(uuid: $uuid) {
          id
          uuid
          tradeType
          state
          buyer {
            user { uuid email givenName familyName }
            community { uuid title }
            residualId
            tradePoint {
              uuid
              type
              meter {
                uuid
                identifier
                title
                property {
                  uuid
                  title
                }
              }
            }
          }
          seller {
            user { uuid email givenName familyName }
            community { uuid title }
            residualId
            tradePoint {
              uuid
              type
              meter {
                uuid
                identifier
                title
                property {
                  uuid
                  title
                }
              }
            }
          }
          clauses {
            edges {
              node {
                price
                ignoreDaylightSavings
                ignorePublicHolidays
                timezone
                publicHolidayRegion
                uuid
                monthsOfYear
                daysOfWeek
                timesOfDay {
                  start { hours minutes }
                  finish { hours minutes }
                }
              }
            }
          }
          proposedAt
          proposedBy { uuid email givenName familyName }
          acceptedAt
          acceptedBy { uuid email givenName familyName }
          rejectedAt
          rejectedBy { uuid email givenName familyName }
          cancelledAt
          cancelledBy { uuid email givenName familyName }
          closedAt
          closedBy { uuid email givenName familyName }
        }
      }
    `,
  },
));
